import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db, storage } from "../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import QRCodeStyling from "qr-code-styling";
import {
  FiLink, FiType, FiWifi, FiDownload, FiSave, FiTrash2, FiImage,
  FiCheckCircle, FiAlertCircle, FiZap,
} from "react-icons/fi";
import styles from "../styles/QRManagerPage.module.css";

/* ── helpers ── */
const buildWifiString = ({ ssid, password, encryption }) =>
  `WIFI:T:${encryption || "WPA"};S:${ssid || ""};P:${password || ""};;`;

const safeHex = (v, fallback) => {
  const s = String(v || "").trim();
  return /^#([0-9a-f]{3}){1,2}$/i.test(s) ? s : fallback;
};

const BODY_TYPES      = ["square", "dots", "rounded", "classy", "classy-rounded", "extra-rounded"];
const EYE_FRAME_TYPES = ["square", "extra-rounded", "dot", "classy", "classy-rounded"];
const EYE_BALL_TYPES  = ["square", "dot"];

const TYPE_OPTIONS = [
  { id: "url",  label: "URL",   Icon: FiLink  },
  { id: "text", label: "Text",  Icon: FiType  },
  { id: "wifi", label: "Wi-Fi", Icon: FiWifi  },
];

const typeBadgeColor = t =>
  t === "url" ? "#4a90e2" : t === "wifi" ? "#10b981" : "#f5a623";

export default function QRManagerPage() {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);

  /* form */
  const [title, setTitle] = useState("");
  const [type,  setType]  = useState("url");
  const [value, setValue] = useState("");
  const [wifi,  setWifi]  = useState({ ssid: "", password: "", encryption: "WPA" });

  /* style */
  const [size,   setSize]   = useState(420);
  const [margin, setMargin] = useState(10);
  const [fg,     setFg]     = useState("#111827");
  const [bg,     setBg]     = useState("#ffffff");

  const [bodyType,     setBodyType]     = useState("square");
  const [eyeFrameType, setEyeFrameType] = useState("square");
  const [eyeBallType,  setEyeBallType]  = useState("square");

  const [useGradient, setUseGradient] = useState(false);
  const [g1, setG1] = useState("#111827");
  const [g2, setG2] = useState("#2563eb");

  /* logo */
  const [logoUrl,      setLogoUrl]      = useState(""); // remote URL → saved to Firestore
  const [logoDataUrl,  setLogoDataUrl]  = useState(""); // local base64 → used in QR preview (avoids CORS)
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoScale,    setLogoScale]    = useState(0.22);
  const [logoPadding,  setLogoPadding]  = useState(10);
  const [hideBgDots,   setHideBgDots]   = useState(true);

  /* ui */
  const [selected, setSelected] = useState(null);
  const [busy,     setBusy]     = useState(false);
  const [msg,      setMsg]      = useState({ text: "", kind: "" });

  const previewRef = useRef(null);
  const qrRef      = useRef(null);

  /* auto-dismiss message */
  useEffect(() => {
    if (!msg.text) return;
    const t = setTimeout(() => setMsg({ text: "", kind: "" }), 4000);
    return () => clearTimeout(t);
  }, [msg.text]);

  const ok  = text => setMsg({ text, kind: "ok"  });
  const err = text => setMsg({ text, kind: "err" });

  /* ── data string ── */
  const dataString = useMemo(
    () => (type === "wifi" ? buildWifiString(wifi) : value || ""),
    [type, value, wifi]
  );

  /* ── Firestore list ── */
  useEffect(() => {
    const q = query(collection(db, "tools_qrcodes"), orderBy("createdAt", "desc"));
    return onSnapshot(
      q,
      snap => { setItems(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(false); },
      e    => { console.error(e); setLoading(false); }
    );
  }, []);

  /* ── QR options ── */
  const qrOptions = useMemo(() => {
    const px    = Math.max(220, Math.min(1200, Number(size)  || 420));
    const dark  = safeHex(fg, "#111827");
    const light = safeHex(bg, "#ffffff");
    const gradient = useGradient
      ? { type: "linear", rotation: 0, colorStops: [
            { offset: 0, color: safeHex(g1, "#111827") },
            { offset: 1, color: safeHex(g2, "#2563eb") },
          ] }
      : null;

    return {
      width: px, height: px, type: "svg",
      data: String(dataString || "").trim() || " ",
      margin: Math.max(0, Math.min(40, Number(margin) || 10)),
      backgroundOptions: { color: light },
      dotsOptions:         gradient ? { type: bodyType,     gradient } : { type: bodyType,     color: dark },
      cornersSquareOptions: gradient ? { type: eyeFrameType, gradient } : { type: eyeFrameType, color: dark },
      cornersDotOptions:    gradient ? { type: eyeBallType,  gradient } : { type: eyeBallType,  color: dark },
      image: logoDataUrl || undefined,
      imageOptions: {
        margin:              Math.max(0, Math.min(40,   Number(logoPadding) || 10)),
        imageSize:           Math.max(0.12, Math.min(0.32, Number(logoScale) || 0.22)),
        hideBackgroundDots:  !!hideBgDots,
      },
      qrOptions: { errorCorrectionLevel: "H" },
    };
  }, [dataString, size, margin, fg, bg, bodyType, eyeFrameType, eyeBallType,
      useGradient, g1, g2, logoDataUrl, logoPadding, logoScale, hideBgDots]);

  /* ── init preview ── */
  useEffect(() => {
    if (!qrRef.current) {
      qrRef.current = new QRCodeStyling(qrOptions);
      if (previewRef.current) {
        previewRef.current.innerHTML = "";
        qrRef.current.append(previewRef.current);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (qrRef.current) qrRef.current.update(qrOptions);
  }, [qrOptions]);

  /* ── actions ── */
  const uploadLogo = async file => {
    if (!file) return;

    // Read as data URL immediately — no CORS issue, preview works instantly
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload  = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    setLogoDataUrl(dataUrl);

    setLogoUploading(true);
    try {
      const cleanName  = file.name.replace(/[^\w.-]+/g, "_");
      const storageRef = ref(storage, `tools/qr-logos/${Date.now()}_${cleanName}`);
      await uploadBytes(storageRef, file);
      setLogoUrl(await getDownloadURL(storageRef));
      ok("Logo uploaded.");
    } catch { err("Failed to upload logo."); }
    finally  { setLogoUploading(false); }
  };

  const createQr = async () => {
    if (!String(dataString || "").trim()) return err("Please enter QR data.");
    setBusy(true);
    try {
      const payload = {
        title: title.trim() || "Untitled", type,
        data:  String(dataString || "").trim(),
        style: {
          size: Number(size) || 420, margin: Number(margin) || 10,
          fg: safeHex(fg, "#111827"), bg: safeHex(bg, "#ffffff"),
          bodyType, eyeFrameType, eyeBallType,
          useGradient: !!useGradient, g1: safeHex(g1, "#111827"), g2: safeHex(g2, "#2563eb"),
          logoUrl: logoUrl || "",
          logoScale: Number(logoScale) || 0.22,
          logoPadding: Number(logoPadding) || 10,
          hideBgDots: !!hideBgDots,
        },
        createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
      };
      const docRef = await addDoc(collection(db, "tools_qrcodes"), payload);
      setSelected({ id: docRef.id, ...payload });
      ok("QR saved successfully.");
      setTitle(""); setType("url"); setValue(""); setWifi({ ssid: "", password: "", encryption: "WPA" });
    } catch { err("Failed to save QR."); }
    finally  { setBusy(false); }
  };

  const removeQr = async id => {
    if (!id) return;
    setBusy(true);
    try {
      await deleteDoc(doc(db, "tools_qrcodes", id));
      if (selected?.id === id) setSelected(null);
      ok("QR deleted.");
    } catch { err("Failed to delete."); }
    finally  { setBusy(false); }
  };

  const renameQr = async (id, newTitle) => {
    try {
      await updateDoc(doc(db, "tools_qrcodes", id), { title: newTitle || "Untitled", updatedAt: serverTimestamp() });
      ok("Renamed.");
    } catch { err("Failed to rename."); }
  };

  const loadFromSaved = async it => {
    setSelected(it);
    setTitle(it?.title || "");
    setType(it?.type  || "url");
    setValue(it?.data || "");
    const s = it?.style || {};
    setSize(s.size ?? 420);       setMargin(s.margin ?? 10);
    setFg(s.fg ?? "#111827");     setBg(s.bg ?? "#ffffff");
    setBodyType(s.bodyType ?? "square");
    setEyeFrameType(s.eyeFrameType ?? "square");
    setEyeBallType(s.eyeBallType  ?? "square");
    setUseGradient(!!s.useGradient);
    setG1(s.g1 ?? "#111827");     setG2(s.g2 ?? "#2563eb");
    setLogoScale(s.logoScale  ?? 0.22);
    setLogoPadding(s.logoPadding ?? 10);
    setHideBgDots(s.hideBgDots ?? true);

    const remoteUrl = s.logoUrl ?? "";
    setLogoUrl(remoteUrl);
    setLogoDataUrl(""); // clear while fetching

    if (remoteUrl) {
      try {
        const res  = await fetch(remoteUrl);
        const blob = await res.blob();
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload  = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        setLogoDataUrl(dataUrl);
      } catch {
        // fetch failed (CORS / network) — render QR without logo
        setLogoDataUrl("");
      }
    }
  };

  const downloadPng = () => qrRef.current?.download({ name: `qr_${(title||"qr").replace(/\s+/g,"_")}`, extension: "png" });
  const downloadSvg = () => qrRef.current?.download({ name: `qr_${(title||"qr").replace(/\s+/g,"_")}`, extension: "svg" });

  /* ══════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════ */
  return (
    <div className={styles.page}>

      {/* ── Page Header ── */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>QR</div>
        <div>
          <h1 className={styles.pageTitle}>QR Code Manager</h1>
          <p className={styles.pageSubtitle}>Create, customize, and export QR codes with your branding.</p>
        </div>
      </div>

      {/* ── Alert ── */}
      {msg.text && (
        <div className={`${styles.alert} ${msg.kind === "err" ? styles.alertErr : styles.alertOk}`}>
          {msg.kind === "err"
            ? <FiAlertCircle size={15} />
            : <FiCheckCircle size={15} />}
          <span>{msg.text}</span>
        </div>
      )}

      <div className={styles.grid}>

        {/* ══ LEFT: Editor ══ */}
        <div className={styles.editorCol}>

          {/* Section 1 — Data */}
          <div className={styles.card}>
            <div className={styles.secHeader}>
              <span className={styles.secDot} style={{ background: "#4a90e2" }} />
              <span className={styles.secLabel}>QR Content</span>
            </div>

            <div className={styles.typeTabs}>
              {TYPE_OPTIONS.map(({ id, label, Icon }) => (
                <button key={id} type="button"
                  className={`${styles.typeTab} ${type === id ? styles.typeTabActive : ""}`}
                  onClick={() => setType(id)}>
                  <Icon size={14} />
                  {label}
                </button>
              ))}
            </div>

            <div className={styles.field}>
              <label>Title</label>
              <input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Restaurant Menu" />
            </div>

            {type !== "wifi" ? (
              <div className={styles.field}>
                <label>{type === "url" ? "URL" : "Text"}</label>
                <input
                  value={value}
                  onChange={e => setValue(e.target.value)}
                  placeholder={type === "url" ? "https://example.com" : "Any text…"}
                />
              </div>
            ) : (
              <div className={styles.wifiGrid}>
                <div className={styles.field}>
                  <label>Network Name (SSID)</label>
                  <input value={wifi.ssid} onChange={e => setWifi(p => ({ ...p, ssid: e.target.value }))} placeholder="My WiFi" />
                </div>
                <div className={styles.field}>
                  <label>Password</label>
                  <input type="password" value={wifi.password}
                    onChange={e => setWifi(p => ({ ...p, password: e.target.value }))} />
                </div>
                <div className={styles.field} style={{ gridColumn: "1/-1" }}>
                  <label>Security</label>
                  <select value={wifi.encryption} onChange={e => setWifi(p => ({ ...p, encryption: e.target.value }))}>
                    <option value="WPA">WPA / WPA2</option>
                    <option value="WEP">WEP</option>
                    <option value="nopass">Open (No Password)</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Section 2 — Colors & Size */}
          <div className={styles.card}>
            <div className={styles.secHeader}>
              <span className={styles.secDot} style={{ background: "#f5a623" }} />
              <span className={styles.secLabel}>Colors & Size</span>
            </div>

            <div className={styles.colorRow}>
              <div className={styles.field}>
                <label>Foreground</label>
                <div className={styles.colorPicker}>
                  <input type="color" value={safeHex(fg, "#111827")} onChange={e => setFg(e.target.value)} />
                  <input type="text" value={fg} onChange={e => setFg(e.target.value)} placeholder="#111827" />
                </div>
              </div>
              <div className={styles.field}>
                <label>Background</label>
                <div className={styles.colorPicker}>
                  <input type="color" value={safeHex(bg, "#ffffff")} onChange={e => setBg(e.target.value)} />
                  <input type="text" value={bg} onChange={e => setBg(e.target.value)} placeholder="#ffffff" />
                </div>
              </div>
            </div>

            <div className={styles.field}>
              <label>Size — <strong>{size} px</strong></label>
              <input type="range" min="220" max="1200" step="10" value={size}
                onChange={e => setSize(e.target.value)} className={styles.slider} />
              <div className={styles.sliderTicks}><span>220 px</span><span>1200 px</span></div>
            </div>

            <div className={styles.field}>
              <label>Margin — <strong>{margin} px</strong></label>
              <input type="range" min="0" max="40" step="1" value={margin}
                onChange={e => setMargin(e.target.value)} className={styles.slider} />
              <div className={styles.sliderTicks}><span>0</span><span>40 px</span></div>
            </div>

            <label className={styles.toggleRow}>
              <input type="checkbox" checked={useGradient} onChange={e => setUseGradient(e.target.checked)} />
              <FiZap size={13} style={{ color: "#f5a623" }} />
              <span>Enable gradient</span>
            </label>

            {useGradient && (
              <div className={styles.colorRow} style={{ marginTop: 12 }}>
                <div className={styles.field}>
                  <label>Gradient Start</label>
                  <div className={styles.colorPicker}>
                    <input type="color" value={safeHex(g1, "#111827")} onChange={e => setG1(e.target.value)} />
                    <input type="text" value={g1} onChange={e => setG1(e.target.value)} placeholder="#111827" />
                  </div>
                </div>
                <div className={styles.field}>
                  <label>Gradient End</label>
                  <div className={styles.colorPicker}>
                    <input type="color" value={safeHex(g2, "#2563eb")} onChange={e => setG2(e.target.value)} />
                    <input type="text" value={g2} onChange={e => setG2(e.target.value)} placeholder="#2563eb" />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Section 3 — Shape */}
          <div className={styles.card}>
            <div className={styles.secHeader}>
              <span className={styles.secDot} style={{ background: "#8b5cf6" }} />
              <span className={styles.secLabel}>Shape Design</span>
            </div>

            <div className={styles.shapeBlock}>
              <div className={styles.shapeLabel}>Body</div>
              <div className={styles.pillRow}>
                {BODY_TYPES.map(t => (
                  <button key={t} type="button"
                    className={`${styles.pill} ${bodyType === t ? styles.pillActive : ""}`}
                    onClick={() => setBodyType(t)}>
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.shapeBlock}>
              <div className={styles.shapeLabel}>Eye Frame</div>
              <div className={styles.pillRow}>
                {EYE_FRAME_TYPES.map(t => (
                  <button key={t} type="button"
                    className={`${styles.pill} ${eyeFrameType === t ? styles.pillActive : ""}`}
                    onClick={() => setEyeFrameType(t)}>
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.shapeBlock} style={{ marginBottom: 0 }}>
              <div className={styles.shapeLabel}>Eye Ball</div>
              <div className={styles.pillRow}>
                {EYE_BALL_TYPES.map(t => (
                  <button key={t} type="button"
                    className={`${styles.pill} ${eyeBallType === t ? styles.pillActive : ""}`}
                    onClick={() => setEyeBallType(t)}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Section 4 — Logo */}
          <div className={styles.card}>
            <div className={styles.secHeader}>
              <span className={styles.secDot} style={{ background: "#10b981" }} />
              <span className={styles.secLabel}>Logo</span>
            </div>

            <label className={styles.uploadArea}>
              <FiImage size={22} />
              <span>{logoUploading ? "Uploading…" : "Click to upload a logo image"}</span>
              <small>PNG, JPG, SVG — displayed in the center of the QR</small>
              <input type="file" accept="image/*" style={{ display: "none" }}
                onChange={e => uploadLogo(e.target.files?.[0])} disabled={logoUploading} />
            </label>

            {logoUrl && (
              <div className={styles.logoRow}>
                <img className={styles.logoThumb} src={logoUrl} alt="logo" />
                <div style={{ flex: 1 }}>
                  <div className={styles.logoOk}>Logo set ✓</div>
                </div>
                <button type="button" className={styles.btnRemoveLogo} onClick={() => { setLogoUrl(""); setLogoDataUrl(""); }}>
                  Remove
                </button>
              </div>
            )}

            <div className={styles.field}>
              <label>Logo Size — <strong>{(Number(logoScale) * 100).toFixed(0)}%</strong></label>
              <input type="range" min="12" max="32" step="1"
                value={Math.round(Number(logoScale) * 100)}
                onChange={e => setLogoScale(Number(e.target.value) / 100)}
                className={styles.slider} />
              <div className={styles.sliderTicks}><span>12%</span><span>32%</span></div>
            </div>

            <div className={styles.field}>
              <label>Logo Padding — <strong>{logoPadding} px</strong></label>
              <input type="range" min="0" max="40" step="1" value={logoPadding}
                onChange={e => setLogoPadding(e.target.value)} className={styles.slider} />
              <div className={styles.sliderTicks}><span>0</span><span>40 px</span></div>
            </div>

            <label className={styles.checkRow}>
              <input type="checkbox" checked={hideBgDots} onChange={e => setHideBgDots(e.target.checked)} />
              <span>Hide QR dots behind logo <em>(recommended)</em></span>
            </label>
          </div>

          {/* Actions */}
          <div className={styles.actionsRow}>
            <button className={styles.btnSave} onClick={createQr} disabled={busy}>
              <FiSave size={15} />
              {busy ? "Saving…" : "Save QR"}
            </button>
            <button className={styles.btnDl} onClick={downloadPng} type="button">
              <FiDownload size={15} /> PNG
            </button>
            <button className={styles.btnDl} onClick={downloadSvg} type="button">
              <FiDownload size={15} /> SVG
            </button>
          </div>
        </div>

        {/* ══ RIGHT: Preview + Saved List ══ */}
        <div className={styles.rightCol}>

          {/* Preview */}
          <div className={styles.card}>
            <div className={styles.secHeader}>
              <span className={styles.secDot} style={{ background: "#4a90e2" }} />
              <span className={styles.secLabel}>Live Preview</span>
            </div>
            <div className={styles.canvasBox}>
              <div ref={previewRef} className={styles.canvasInner} />
            </div>
            {dataString && (
              <div className={styles.dataMeta}>
                <span className={styles.dataLabel}>DATA</span>
                <code className={styles.dataCode}>
                  {String(dataString).slice(0, 90)}{dataString.length > 90 ? "…" : ""}
                </code>
              </div>
            )}
          </div>

          {/* Saved list */}
          <div className={styles.card}>
            <div className={styles.secHeader}>
              <span className={styles.secDot} style={{ background: "#f5a623" }} />
              <span className={styles.secLabel}>Saved QR Codes</span>
              <span className={styles.countChip}>{items.length}</span>
            </div>

            {loading ? (
              <div className={styles.placeholder}>Loading…</div>
            ) : items.length === 0 ? (
              <div className={styles.placeholder}>
                <div className={styles.placeholderIcon}>📭</div>
                <p>No saved QR codes yet.<br />Create one above!</p>
              </div>
            ) : (
              <div className={styles.list}>
                {items.map(it => (
                  <button key={it.id} type="button"
                    className={`${styles.listItem} ${selected?.id === it.id ? styles.listItemActive : ""}`}
                    onClick={() => loadFromSaved(it)}>
                    <span className={styles.typeBadge} style={{ background: typeBadgeColor(it.type) }}>
                      {String(it.type || "qr").toUpperCase()}
                    </span>
                    <div className={styles.listBody}>
                      <div className={styles.listTitle}>{it.title || "Untitled"}</div>
                      <div className={styles.listSub}>
                        {String(it.data || "").slice(0, 38)}{(it.data || "").length > 38 ? "…" : ""}
                      </div>
                    </div>
                    {selected?.id === it.id && <span className={styles.checkMark}>✓</span>}
                  </button>
                ))}
              </div>
            )}

            {selected && (
              <div className={styles.selectedPanel}>
                <div className={styles.selectedRow}>
                  <div className={styles.field} style={{ flex: 1, marginBottom: 0 }}>
                    <label>Rename</label>
                    <input className={styles.renameInput}
                      defaultValue={selected.title || ""}
                      onBlur={e => renameQr(selected.id, e.target.value.trim() || "Untitled")} />
                  </div>
                  <button className={styles.btnDelete} onClick={() => removeQr(selected.id)} disabled={busy}>
                    <FiTrash2 size={14} /> Delete
                  </button>
                </div>
                <p className={styles.scanTip}>
                  💡 Keep logo size ≤ 25% and use high-contrast colors for reliable scanning.
                </p>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
